import type { Location } from 'react-router-dom';

/** Every entity id in this app is a UUID primary key, so a UUID segment is never a tab name. */
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Sections whose tabs are not scoped to an entity, so they group by the section alone.
 *
 * Entity dashboards look like `<section>/<entity-uuid>/<tab>`; these look like `<section>/<tab>`
 * with no id to key on. Add a section here when it gains a tab row of that shape.
 */
const ENTITYLESS_TAB_SECTIONS = new Set(['profile']);

/**
 * Scroll-restoration key that groups a screen's tabs under one entry.
 *
 * `<ScrollRestoration />` saves the offset under this key when leaving a location and restores it
 * when arriving at one with the same key. Giving every tab of a screen the same key therefore
 * keeps your place when switching tab, instead of resetting to the top.
 *
 * The intended mechanism for this is `preventScrollReset` on the tab links, but it cannot work
 * here: `useNavigate` only forwards that option to the data router when `RouteContext.isDataRoute`
 * is true, and the dashboards render inside `HomeRoutes` — a descendant `<Routes>` mounted at
 * `/*`, which is not a data route. Its links get the plain history navigator, which drops the
 * option. The tab links still declare `preventScrollReset` so they behave correctly if
 * `HomeRoutes` is ever folded into the data router's own route tree.
 *
 * Returning `null` means "no opinion" — React Router falls back to `location.key`, its per-history
 * entry default, so ordinary navigation and back/forward restore are unaffected.
 *
 * The entity-dashboard shape is required exactly — three or more segments, a final segment that is
 * not an id, and a parent ending in one. That is what keeps `/customers/:idA` → `/customers/:idB`
 * (a different entity, not a tab) and `/subscriptions/:id/subscription-item/:itemId` (drilling in)
 * out of the grouping.
 */
export const getTabScrollKey = (location: Location): string | null => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    const lastSegment = segments[segments.length - 1];
    if (UUID_SEGMENT.test(lastSegment)) return null;

    // `<section>/<tab>`, e.g. /profile, /profile/password, /profile/settings.
    const [section] = segments;
    if (segments.length <= 2 && ENTITYLESS_TAB_SECTIONS.has(section)) return section;

    // `<section>/<entity-uuid>/<tab>`, e.g. /customers/:id/subscriptions.
    if (segments.length < 3) return null;

    const parent = segments.slice(0, -1);
    if (!UUID_SEGMENT.test(parent[parent.length - 1])) return null;

    return parent.join('/');
};
